import type { Size } from "odyssey-shared";
import type { TilePoint } from "./geometry.ts";

/**
 * The world map's pin (ADR-100 decision 11): one point the player dropped to say where they are going, kept on the device
 * per world, map and character and never sent. Stored as the text `x,y` in tiles to a sixteenth; anything else reads as
 * no pin. The pin is cleared once the character's feet come within `PIN_REACH_TILES` of it.
 */

/**
 * How near the character's feet must come to the pin for it to clear, in tiles.
 */
export const PIN_REACH_TILES = 2;

const SIXTEENTHS = 16;
const KEY_PREFIX = "odyssey.worldmap.pin.";

/**
 * The storage key a pin lives under; each part is escaped, so no two triples share a key.
 *
 * @param world The world's id.
 * @param map The map's id.
 * @param character The character's id.
 * @returns The key.
 */
export function pinKey(world: string, map: string, character: string): string {
  return `${KEY_PREFIX}${[world, map, character].map(encodeURIComponent).join("/")}`;
}

/**
 * A point rounded to the sixteenth of a tile and kept on the map.
 *
 * @param point The point in tiles.
 * @param bounds The map's size in tiles.
 * @returns The pin, from 0 to the last sixteenth before the map's edge.
 */
export function pinAt(point: TilePoint, bounds: Size): TilePoint {
  const largest = (axis: number): number => axis - 1 / SIXTEENTHS;
  const keep = (value: number, size: number): number => Math.max(0, Math.min(largest(size), Math.round(value * SIXTEENTHS) / SIXTEENTHS));
  return { x: keep(point.x, bounds.width), y: keep(point.y, bounds.height) };
}

/**
 * The text a pin is stored as.
 *
 * @param pin The pin.
 * @returns `x,y`.
 */
export function serialisePin(pin: TilePoint): string {
  return `${pin.x},${pin.y}`;
}

/**
 * Reads a stored pin.
 *
 * @param raw The stored text, or null when nothing is stored.
 * @param bounds The map's size in tiles.
 * @returns The pin, or null for nothing stored, an empty text, or anything that is not two finite numbers on the map.
 */
export function parsePin(raw: string | null, bounds: Size): TilePoint | null {
  if (raw === null) {
    return null;
  }
  const parts = raw.split(",");
  if (parts.length !== 2) {
    return null;
  }
  const x = Number(parts[0]);
  const y = Number(parts[1]);
  if (parts[0] === "" || parts[1] === "" || !Number.isFinite(x) || !Number.isFinite(y) || x < 0 || y < 0 || x >= bounds.width || y >= bounds.height) {
    return null;
  }
  return { x, y };
}

/**
 * Whether the character's feet are within the pin's reach.
 *
 * @param pin The pin in tiles.
 * @param feet The character's feet in tiles.
 * @returns True within `PIN_REACH_TILES`, the edge included.
 */
export function pinReached(pin: TilePoint, feet: TilePoint): boolean {
  return Math.hypot(pin.x - feet.x, pin.y - feet.y) <= PIN_REACH_TILES;
}
